import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import {
  AlertTriangle,
  ArrowRight,
  Briefcase,
  Download,
  GraduationCap,
  LogIn,
  Pencil,
  Trash2,
  User,
  UserPlus,
} from "lucide-react";
import { Link } from "@tanstack/react-router";
import PageHeader from "@/components/shared/PageHeader";
import StatusChip from "@/components/shared/StatusChip";
import { TierBadge } from "@/components/shared/TierBadge";
import SectionCard from "@/components/shared/SectionCard";
import StatCard from "@/components/shared/StatCard";
import EmptyState from "@/components/shared/EmptyState";
import { IconAction } from "@/components/shared/IconAction";
import { SearchInput } from "@/components/shared/SearchInput";
import { formatDate } from "@/utils/formatters";
import {
  useAdminStudents,
  useUpdateStudent,
  useDeleteUser,
  useEnrollStudent,
  useImpersonateUser,
} from "@/hooks/useAdminData";
import { useAuthStore } from "@/store/authStore";
import { resolveAccountId } from "@/utils/adminRecords";
import { TableSkeleton } from "@/components/shared/Skeletons";
import { getFileUrl } from "@/lib/api-client";
import {
  useUniversities,
  useProgrammes,
  UniversityOption,
  ProgrammeOption,
} from "@/hooks/useReferenceData";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Combobox } from "@/components/ui/combobox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Field } from "@/components/forms";
import { PeopleAvatar, PeopleTooltipAction } from "@/components/admin/people-ui";

export const Route = createFileRoute("/admin/students/")({
  component: AdminStudentsPage,
});

const CURRENT_VALUE = "__current__";

function AdminStudentsPage() {
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [enrollDialogOpen, setEnrollDialogOpen] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState<any>(null);
  const { user } = useAuthStore();

  const [editUniversityId, setEditUniversityId] = useState<number | null>(null);
  const [editCourse, setEditCourse] = useState("");
  const [uniSearch, setUniSearch] = useState("");
  const [progSearch, setProgSearch] = useState("");
  const { data: universities, isLoading: uniLoading } = useUniversities(
    uniSearch,
    editUniversityId,
  );
  const { data: programmes, isLoading: progLoading } = useProgrammes(
    editUniversityId || undefined,
    progSearch,
  );

  const { data: studentsData, isLoading } = useAdminStudents();
  const updateStudent = useUpdateStudent();
  const deleteUser = useDeleteUser();
  const impersonate = useImpersonateUser();
  const enrollStudent = useEnrollStudent();

  const studentList = studentsData ?? [];

  const [enrollName, setEnrollName] = useState("");
  const [enrollEmail, setEnrollEmail] = useState("");
  const [enrollPassword, setEnrollPassword] = useState("");
  const [enrollUniversity, setEnrollUniversity] = useState("");
  const [enrollCourse, setEnrollCourse] = useState("");
  const [enrollYear, setEnrollYear] = useState("1");

  const filteredStudents = studentList.filter((s: any) => {
    const name = s.name || "";
    const email = s.email || "";
    const matchesSearch =
      name.toLowerCase().includes(search.toLowerCase()) ||
      email.toLowerCase().includes(search.toLowerCase());
    const matchesStatus = statusFilter === "all" || s.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const handleEdit = (student: any) => {
    setSelectedStudent({ ...student });
    setEditUniversityId(null);
    setEditCourse(student.course || "");
    setEditDialogOpen(true);
  };

  const handleUpdate = async () => {
    if (!selectedStudent) return;

    try {
      await updateStudent.mutateAsync({
        id: selectedStudent.user_id || selectedStudent.id,
        payload: {
          name: selectedStudent.name,
          university: selectedStudent.university,
          course: selectedStudent.course,
          status: selectedStudent.status,
          year: selectedStudent.year,
          membership_tier: selectedStudent.membership_tier || "free",
        },
      });
      setEditDialogOpen(false);
    } catch (err) {
      console.error("Failed to update student:", err);
    }
  };

  const handleDelete = (student: any) => {
    setSelectedStudent(student);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = () => {
    const userId = resolveAccountId(selectedStudent);
    if (!userId) return;
    deleteUser.mutate(userId, { onSettled: () => setDeleteDialogOpen(false) });
  };

  // --- Institution / programme combobox wiring (same semantics as the old Autocomplete) ---
  const uniId = (u: UniversityOption) => u.id || (u as any).ID;
  const showCurrentUni = Boolean(selectedStudent?.university && !editUniversityId);
  const universityOptions = [
    ...(showCurrentUni
      ? [{ value: CURRENT_VALUE, label: selectedStudent.university as string }]
      : []),
    ...(universities || []).map((u: UniversityOption) => ({
      value: String(uniId(u)),
      label: u.name,
      description: [u.abbreviation, u.location, u.type].filter(Boolean).join(" • "),
    })),
  ];
  const universityValue = editUniversityId
    ? String(editUniversityId)
    : showCurrentUni
      ? CURRENT_VALUE
      : null;

  const handleUniversityChange = (v: string | null) => {
    if (v === CURRENT_VALUE) return;
    const value = v ? (universities || []).find((u: UniversityOption) => String(uniId(u)) === v) : null;
    const id = value ? uniId(value) : null;
    setEditUniversityId(id);
    setEditCourse("");
    setProgSearch("");
    setSelectedStudent((prev: any) => ({ ...prev, university: value?.name || "" }));
  };

  const matchedProgramme = programmes?.find((p: ProgrammeOption) => p.name === editCourse);
  const programmeOptions = [
    ...(editCourse && !matchedProgramme ? [{ value: CURRENT_VALUE, label: editCourse }] : []),
    ...(programmes || []).map((p: ProgrammeOption) => ({
      value: String(p.id),
      label: p.name,
      description: `${p.award_level} ${p.duration ? `• ${p.duration}` : ""}`.trim(),
    })),
  ];
  const programmeValue = matchedProgramme
    ? String(matchedProgramme.id)
    : editCourse
      ? CURRENT_VALUE
      : null;

  const handleProgrammeChange = (v: string | null) => {
    if (v === CURRENT_VALUE) return;
    const value = v ? (programmes || []).find((p: ProgrammeOption) => String(p.id) === v) : null;
    setEditCourse(value?.name || "");
    setSelectedStudent((prev: any) => ({ ...prev, course: value?.name || "" }));
  };

  const hasUniversity = Boolean(editUniversityId || selectedStudent?.university);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Student directory"
        subtitle="Manage student enrollments, monitor placement progress, and review academic readiness."
        breadcrumbs={[{ label: "Dashboard", path: "/admin/dashboard" }, { label: "Students" }]}
        action={
          <>
            <Button variant="outline">
              <Download />
              Export
            </Button>
            <Button onClick={() => setEnrollDialogOpen(true)}>
              <UserPlus />
              Enroll new student
            </Button>
          </>
        }
      />

      <div className="grid grid-cols-1 gap-3 md:gap-4 lg:grid-cols-3">
        <StatCard label="Total students" value={studentList.length} icon={<User />} />
        <StatCard
          label="Active placements"
          value={studentList.filter((s: any) => s.status === "active").length}
          icon={<GraduationCap />}
        />
        <StatCard
          label="Pending readiness"
          value={studentList.filter((s: any) => s.status === "pending").length}
          icon={<Briefcase />}
        />
      </div>

      <SectionCard
        title="Institutional student roster"
        description={`${filteredStudents.length} of ${studentList.length} students`}
        noPadding
        actions={
          <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:items-center">
            <SearchInput
              value={search}
              onChange={setSearch}
              placeholder="Search students…"
              aria-label="Search students"
            />
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="h-8 w-full sm:w-48" aria-label="Deployment status">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="active">Active placement</SelectItem>
                <SelectItem value="pending">Awaiting matching</SelectItem>
                <SelectItem value="completed">Graduated / Completed</SelectItem>
                <SelectItem value="rejected">Rejected / Restricted</SelectItem>
              </SelectContent>
            </Select>
          </div>
        }
      >
        {isLoading ? (
          <div className="p-4">
            <TableSkeleton rows={5} columns={4} />
          </div>
        ) : filteredStudents.length === 0 ? (
          <EmptyState
            bare
            icon={<User />}
            title="No students found"
            description={
              search || statusFilter !== "all"
                ? "Try a different search or status filter."
                : "Enrolled students will appear here."
            }
          />
        ) : (
          <Table className="min-w-[960px]">
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="pl-4 md:pl-5">Student</TableHead>
                <TableHead>Institution</TableHead>
                <TableHead>Academic program</TableHead>
                <TableHead className="text-right">Field hours</TableHead>
                <TableHead>Account state</TableHead>
                <TableHead>Enrolled on</TableHead>
                <TableHead className="pr-4 text-right md:pr-5">
                  <span className="sr-only">Actions</span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredStudents.map((s: any) => {
                const accountId = resolveAccountId(s);
                return (
                  <TableRow key={s.id} className="h-11">
                    <TableCell className="py-2 pl-4 md:pl-5">
                      <div className="flex min-w-0 items-center gap-2.5">
                        <PeopleAvatar
                          src={s.avatar_url ? getFileUrl(s.avatar_url) : undefined}
                          name={s.name || s.initial}
                        />
                        <div className="min-w-0">
                          <Link
                            to={`/admin/students/${s.id}` as any}
                            className="block max-w-[220px] truncate font-medium text-foreground hover:underline"
                          >
                            {s.name}
                          </Link>
                          <p className="max-w-[220px] truncate text-caption text-muted-foreground">
                            {s.email}
                          </p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="max-w-[200px] truncate py-2 text-foreground">
                      {s.university}
                    </TableCell>
                    <TableCell className="max-w-[200px] truncate py-2 text-body">
                      {s.course}
                    </TableCell>
                    <TableCell className="py-2 text-right font-mono text-body tabular-nums">
                      {s.hoursLogged}h
                    </TableCell>
                    <TableCell className="py-2">
                      <div className="flex flex-wrap items-center gap-1.5">
                        <StatusChip status={s.status} />
                        <TierBadge
                          tier={(s.membership_tier || "free") as "free" | "verified" | "premium"}
                          verificationStatus={s.verification_status}
                          size="small"
                        />
                      </div>
                    </TableCell>
                    <TableCell className="py-2 whitespace-nowrap text-muted-foreground tabular-nums">
                      {formatDate(s.registeredDate || s.created_at || s.enrolled)}
                    </TableCell>
                    <TableCell className="py-2 pr-4 md:pr-5">
                      <div className="flex items-center justify-end gap-0.5">
                        <PeopleTooltipAction
                          label={`Log in as ${s.name ?? s.email}`}
                          tooltip={
                            accountId
                              ? "Open this student's portal in a new tab"
                              : "This record has no linked account"
                          }
                          icon={<LogIn />}
                          disabled={impersonate.isPending || !accountId}
                          onClick={() =>
                            impersonate.mutate({
                              userId: resolveAccountId(s),
                              name: s.name ?? s.email ?? "this student",
                            })
                          }
                        />
                        <IconAction
                          label="Modify record"
                          icon={<Pencil />}
                          onClick={() => handleEdit(s)}
                        />
                        <IconAction
                          label="Delete student"
                          tone="destructive"
                          icon={<Trash2 />}
                          onClick={() => handleDelete(s)}
                        />
                        <Button
                          variant="ghost"
                          size="icon-sm"
                          className="text-muted-foreground hover:text-foreground"
                          asChild
                        >
                          <Link
                            to={`/admin/students/${s.id}` as any}
                            aria-label={`View ${s.name ?? "student"}`}
                          >
                            <ArrowRight />
                          </Link>
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </SectionCard>

      {/* Edit student dialog */}
      <Dialog open={editDialogOpen} onOpenChange={(o) => !o && setEditDialogOpen(false)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Academic profile update</DialogTitle>
            <DialogDescription>
              Update {selectedStudent?.name || "this student"}&apos;s record, status and tier.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Full name" className="sm:col-span-2">
              <Input
                value={selectedStudent?.name || ""}
                onChange={(e) =>
                  setSelectedStudent((prev: any) => ({ ...prev, name: e.target.value }))
                }
              />
            </Field>
            <Field label="Institution" className="sm:col-span-2">
              <Combobox
                options={universityOptions}
                value={universityValue}
                onChange={handleUniversityChange}
                onSearchChange={setUniSearch}
                loading={uniLoading}
                placeholder="Select institution…"
                searchPlaceholder="Search universities…"
                emptyText="No universities found"
              />
            </Field>
            <Field label="Academic program">
              <Combobox
                options={programmeOptions}
                value={programmeValue}
                onChange={handleProgrammeChange}
                onSearchChange={setProgSearch}
                loading={progLoading}
                disabled={!editUniversityId && !selectedStudent?.university}
                placeholder={hasUniversity ? "Select programme…" : "Select university first"}
                searchPlaceholder="Search programmes…"
                emptyText={hasUniversity ? "No programmes found" : "Select university first"}
              />
            </Field>
            <Field label="Field hours">
              <Input type="number" defaultValue={selectedStudent?.hoursLogged} />
            </Field>
            <Field label="Deployment status">
              {({ id }) => (
                <Select
                  value={selectedStudent?.status || "active"}
                  onValueChange={(v) => setSelectedStudent((prev: any) => ({ ...prev, status: v }))}
                >
                  <SelectTrigger id={id}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active placement</SelectItem>
                    <SelectItem value="pending">Awaiting matching</SelectItem>
                    <SelectItem value="completed">Graduated</SelectItem>
                    <SelectItem value="rejected">Restricted</SelectItem>
                  </SelectContent>
                </Select>
              )}
            </Field>
            <Field
              label="Membership tier"
              hint="Manually grant a tier to test benefits without payment."
            >
              {({ id, describedBy }) => (
                <Select
                  value={selectedStudent?.membership_tier || "free"}
                  onValueChange={(v) =>
                    setSelectedStudent((prev: any) => ({ ...prev, membership_tier: v }))
                  }
                >
                  <SelectTrigger id={id} aria-describedby={describedBy}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="free">Free</SelectItem>
                    <SelectItem value="verified">Verified Student</SelectItem>
                    <SelectItem value="premium">Premium Career</SelectItem>
                  </SelectContent>
                </Select>
              )}
            </Field>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleUpdate} disabled={updateStudent.isPending} loading={updateStudent.isPending}>
              {updateStudent.isPending ? "Updating..." : "Update records"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={(o) => !o && setDeleteDialogOpen(false)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <div className="mb-2 flex size-9 items-center justify-center rounded-md border border-destructive/30 bg-destructive/10 text-destructive max-sm:mx-auto">
              <AlertTriangle className="size-4" aria-hidden />
            </div>
            <DialogTitle>Delete student</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete{" "}
              <span className="font-medium text-foreground">{selectedStudent?.name}</span>? Their
              portal credentials stop working immediately. You can restore them from Trash.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Keep active
            </Button>
            <Button
              variant="destructive"
              onClick={confirmDelete}
              disabled={deleteUser.isPending}
              loading={deleteUser.isPending}
            >
              {deleteUser.isPending ? "Deleting…" : "Delete now"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Enroll new student dialog */}
      <Dialog open={enrollDialogOpen} onOpenChange={(o) => !o && setEnrollDialogOpen(false)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>New student enrollment</DialogTitle>
            <DialogDescription>
              Manually register a student into the system catalog.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Student full name">
              <Input
                placeholder="e.g. John Msigwa"
                value={enrollName}
                onChange={(e) => setEnrollName(e.target.value)}
              />
            </Field>
            <Field label="Institutional email">
              <Input
                type="email"
                placeholder="john@udsm.ac.tz"
                value={enrollEmail}
                onChange={(e) => setEnrollEmail(e.target.value)}
              />
            </Field>
            <Field label="Temporary password">
              <Input
                type="password"
                placeholder="Set initial password"
                value={enrollPassword}
                onChange={(e) => setEnrollPassword(e.target.value)}
              />
            </Field>
            <Field label="Affiliated university">
              <Input
                placeholder="e.g. University of Dar es Salaam"
                value={enrollUniversity}
                onChange={(e) => setEnrollUniversity(e.target.value)}
              />
            </Field>
            <Field label="Academic program / course">
              <Input
                placeholder="e.g. BSc Computer Science"
                value={enrollCourse}
                onChange={(e) => setEnrollCourse(e.target.value)}
              />
            </Field>
            <Field label="Year of study">
              {({ id }) => (
                <Select value={enrollYear} onValueChange={setEnrollYear}>
                  <SelectTrigger id={id}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">Year 1</SelectItem>
                    <SelectItem value="2">Year 2</SelectItem>
                    <SelectItem value="3">Year 3</SelectItem>
                    <SelectItem value="4">Year 4</SelectItem>
                  </SelectContent>
                </Select>
              )}
            </Field>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEnrollDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              loading={enrollStudent.isPending}
              onClick={() => {
                enrollStudent.mutate(
                  {
                    name: enrollName,
                    email: enrollEmail,
                    password: enrollPassword,
                    university: enrollUniversity,
                    course: enrollCourse,
                  },
                  {
                    onSettled: () => {
                      setEnrollDialogOpen(false);
                      setEnrollName("");
                      setEnrollEmail("");
                      setEnrollPassword("");
                      setEnrollUniversity("");
                      setEnrollCourse("");
                      setEnrollYear("1");
                    },
                  },
                );
              }}
            >
              {enrollStudent.isPending ? "Enrolling..." : "Complete enrollment"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
